import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    Alert,
    Dialog, DialogTitle, DialogContent, IconButton, Button,
    Box, Typography, CircularProgress,
    Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { MyDashboardTypes } from '../../../../types/my-dashboard.types';
import { MyDashboardService } from '../../../../services/my-dashboard.service';

const DATE_KEYS = new Set(['lastActive', 'initiatedAt', 'createdAt', 'startDateTime', 'endDateTime']);

const formatCell = (key: string, value: any): string => {
    if (value === undefined || value === null || value === '') return '—';
    if (DATE_KEYS.has(key)) {
        const d = dayjs(value);
        return d.isValid() ? d.format('MMM D, YYYY h:mm A') : String(value);
    }
    return String(value);
};

const METRIC_LABELS: Record<MyDashboardTypes.MetricKey, string> = {
    totalUsers: 'All POM Users',
    activeToday: 'Active Today',
    activeLast7Days: 'Active Last 7 Days',
    activeLast30Days: 'Active Last 30 Days',
    inactiveToday: 'Inactive Today',
    inactiveLast7Days: 'Inactive Last 7 Days',
    inactiveLast30Days: 'Inactive Last 30 Days',
    sosAlerts: 'SOS Alerts',
    checkOnMe: 'Check On Me Alerts',
    fakeCalls: 'Fake Calls',
    shifts: 'Shifts',
    pendingRiskReviews: 'Pending Risk Reviews',
    totalRiskReviews: 'Risk Reviews',
    fieldSafetyReports: 'Field Safety Reports',
    appointments: 'Appointments',
};

interface ColumnConfig {
    key: string;
    header: string;
}

const COLUMN_CONFIGS: Record<string, ColumnConfig[]> = {
    totalUsers: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    activeToday: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    activeLast7Days: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    activeLast30Days: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    inactiveToday: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    inactiveLast7Days: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    inactiveLast30Days: [
        { key: 'name', header: 'Name' },
        { key: 'team', header: 'Team' },
        { key: 'phone', header: 'Phone' },
        { key: 'lastActive', header: 'Last Active' },
    ],
    sosAlerts: [
        { key: 'userName', header: 'User' },
        { key: 'initiatedAt', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
    checkOnMe: [
        { key: 'userName', header: 'User' },
        { key: 'initiatedAt', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
    fakeCalls: [
        { key: 'userName', header: 'User' },
        { key: 'initiatedAt', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
    shifts: [
        { key: 'userName', header: 'User' },
        { key: 'initiatedAt', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
    pendingRiskReviews: [
        { key: 'userName', header: 'User' },
        { key: 'initiatedAt', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
    totalRiskReviews: [
        { key: 'userName', header: 'User' },
        { key: 'initiatedAt', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
    fieldSafetyReports: [
        { key: 'userName', header: 'User' },
        { key: 'createdAt', header: 'Date' },
        { key: 'type', header: 'Type' },
    ],
    appointments: [
        { key: 'userName', header: 'User' },
        { key: 'startDateTime', header: 'Date' },
        { key: 'status', header: 'Status' },
    ],
};

// Maps metric keys to the page + query params for "View in [Page]"
type ViewConfig = { path: string; params: Record<string, string>; label: string; last7Days?: boolean };

const VIEW_IN_PAGE: Partial<Record<MyDashboardTypes.MetricKey, ViewConfig>> = {
    activeToday: { path: '/users', params: { activity: 'today' }, label: 'View in POM Users' },
    activeLast7Days: { path: '/users', params: { activity: '7days' }, label: 'View in POM Users' },
    activeLast30Days: { path: '/users', params: { activity: '30days' }, label: 'View in POM Users' },
    inactiveToday: { path: '/users', params: { activity: 'inactive-today' }, label: 'View in POM Users' },
    inactiveLast7Days: { path: '/users', params: { activity: 'inactive-7days' }, label: 'View in POM Users' },
    inactiveLast30Days: { path: '/users', params: { activity: 'inactive-30days' }, label: 'View in POM Users' },
    sosAlerts: { path: '/alerts', params: { alertType: 'SOS' }, label: 'View in Alerts', last7Days: true },
    checkOnMe: { path: '/alerts', params: { alertType: 'CheckOnMe' }, label: 'View in Alerts', last7Days: true },
    fakeCalls: { path: '/alerts', params: { alertType: 'FakeCall' }, label: 'View in Alerts', last7Days: true },
    pendingRiskReviews: { path: '/alerts', params: { alertType: 'RiskReview', status: 'pending-review' }, label: 'View in Alerts', last7Days: true },
    totalRiskReviews: { path: '/alerts', params: { alertType: 'RiskReview' }, label: 'View in Alerts', last7Days: true },
    appointments: { path: '/appointments', params: {}, label: 'View in Appointments' },
};

interface DrillDownModalProps {
    open: boolean;
    metricKey: MyDashboardTypes.MetricKey | null;
    onClose: () => void;
    /** Fires after MUI's exit transition completes so the parent can safely
     *  null out metricKey without skipping the fade-out animation. */
    onExited?: () => void;
    onRowClick: (metricKey: MyDashboardTypes.MetricKey, record: MyDashboardTypes.DrillDownRecord) => void;
}

export const DrillDownModal: React.FC<DrillDownModalProps> = ({ open, metricKey, onClose, onExited, onRowClick }) => {
    const navigate = useNavigate();
    const { items, page, perPage, total } = useSelector((s: RootState) => s.myDashboard.drillDown);
    const loading = useSelector((s: RootState) => s.myDashboard.loading.drillDown);
    const hasError = useSelector((s: RootState) => s.myDashboard.drillDownError);

    // Don't early-exit on !metricKey — the Dialog must stay mounted with
    // open={false} long enough for MUI's exit transition to run. The parent
    // clears metricKey in onExited. When metricKey is null we still render
    // the Dialog shell but with empty content.
    const columns = metricKey ? (COLUMN_CONFIGS[metricKey] || []) : [];
    const title = metricKey ? (METRIC_LABELS[metricKey] || metricKey) : '';
    const viewConfig = metricKey ? VIEW_IN_PAGE[metricKey] : undefined;

    const handleViewInPage = () => {
        if (!viewConfig) return;
        const params = new URLSearchParams(viewConfig.params);
        if (viewConfig.last7Days) {
            params.set('fromDate', dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
            params.set('toDate', dayjs().format('YYYY-MM-DD'));
        }
        onClose();
        navigate(`${viewConfig.path}?${params.toString()}`);
    };

    const handlePageChange = (_: unknown, newPage: number) => {
        if (!metricKey) return;
        MyDashboardService.fetchDrillDown(metricKey, newPage, perPage);
    };

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!metricKey) return;
        MyDashboardService.fetchDrillDown(metricKey, 0, parseInt(e.target.value, 10));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionProps={{ onExited }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1A2B3C' }}>{title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {viewConfig && (
                        <Button
                            size="small"
                            startIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={handleViewInPage}
                            sx={{
                                textTransform: 'none', fontSize: 12, fontWeight: 600,
                                color: '#206FA5', borderRadius: '6px',
                            }}
                        >
                            {viewConfig.label}
                        </Button>
                    )}
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : hasError ? (
                    <Alert
                        severity="error"
                        action={
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<RefreshIcon />}
                                onClick={() => metricKey && MyDashboardService.fetchDrillDown(metricKey, page, perPage)}
                            >
                                Retry
                            </Button>
                        }
                        sx={{ my: 2 }}
                    >
                        Couldn't load records. Try again in a moment.
                    </Alert>
                ) : items.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography sx={{ color: '#94A3B8', fontSize: 14 }}>
                            No records found.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell key={col.key} sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>
                                            {col.header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((record) => (
                                    <TableRow
                                        key={record.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => metricKey && onRowClick(metricKey, record)}
                                    >
                                        {columns.map((col) => (
                                            <TableCell key={col.key} sx={{ fontSize: 13 }}>
                                                {formatCell(col.key, record[col.key])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {total > perPage && (
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                rowsPerPage={perPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPageOptions={[10, 25, 50]}
                            />
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
